import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { addScriptToDocument } from '@copilot/common/utils/scripts';
import { Config } from '@copilot/common/config';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { setCookie, getCookie, COOKIE_NAMES } from '@copilot/common/utils/session/cookies';
import { OrganizationMemberManager } from '@copilot/data/managers';
import { RootState } from '@copilot/common/store/reducers';

/**
 * HubSpot component to inject the tracking code needed to display popup surveys in the entire app
 */
const HubSpot: FCWithChildren<{}> = () => {
    // do not allow impersonator to interact with support as their client
    // they can still access the support chat when not impersonating
    const activeImpersonator = useSelector((state: RootState) => state.app.impersonator);
    if (!isNil(activeImpersonator)) return null;

    const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);
    useEffect(() => {
        async function injectScriptAndIdToken() {
            try {
                // do not load script for resellers, and only load script once email is available
                const email = activeMember?.email;
                if (!Config.isAgency && !isNil(email)) {
                    const existingToken = getCookie(COOKIE_NAMES.HS_CONVERSATIONS_COOKIE);
                    let identificationToken = existingToken;
                    if (isNil(existingToken) || isEmpty(existingToken.trim())) {
                        identificationToken = await OrganizationMemberManager.getCrmIdToken();

                        // this statement should be unreachable since this response is null when impersonation occurs
                        if (isNil(identificationToken)) {
                            console.error('Unreachable path reached: null id token detected');
                            return;
                        }
                        setCookie(COOKIE_NAMES.HS_CONVERSATIONS_COOKIE, identificationToken, {
                            maxAgeSeconds: 12 * 60 * 60, // 12 hours
                        });
                    }
                    window.hsConversationsSettings = {
                        identificationEmail: email,
                        identificationToken,
                    };
                    // load hubspot tracking script so hubspot is aware of page activity
                    addScriptToDocument('https://js.hs-scripts.com/22312186.js', 'hubspot_script');
                    // use the tracking api to identify the user by making them view a page
                    const _hsq = window._hsq ?? [];
                    _hsq.push([
                        'identify',
                        {
                            email: email,
                        },
                    ]);
                    _hsq.push(['trackPageView']);
                }
            } catch (e) {
                console.error(
                    'An error occurred when trying to inject the crm id token and script.'
                );
                console.error(e);
            }
        }
        injectScriptAndIdToken();
    }, [activeMember?.email]);

    return null;
};

export default HubSpot;
